import React, { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { AlertTriangle, CheckCircle2, Clock3, MapPin, Hammer, X, Flag, Volume2, VolumeX } from "lucide-react";
import { fmtTimeSeconds } from "../lib/datetime";

const POLL_INTERVAL_MS = 30_000;
const SLA_MINUTES = 60;

/**
 * Mounted once inside the authenticated app shell. Polls every 30s for:
 *  - pending NRS alerts the current user must acknowledge → blocking modal
 *  - active callouts where the user is the responder but not yet on site → sticky banner
 *
 * Renders both UIs as fixed-position overlays so any page can host them.
 */
export default function CalloutWatcher() {
  const { user } = useAuth();
  const [pending, setPending] = useState([]);
  const [active, setActive] = useState([]);
  const [dismissedAlertIds, setDismissedAlertIds] = useState(() => new Set());
  const lastPollRef = useRef(0);

  const poll = useCallback(async () => {
    if (!user || !user.id) return;
    try {
      const [p, a] = await Promise.all([
        api.get("/nrs/my-pending"),
        api.get("/nrs/my-active"),
      ]);
      setPending(p.data || []);
      setActive(a.data || []);
      lastPollRef.current = Date.now();
    } catch (e) {
      // 401 etc. — ignore silently, AuthContext handles logout
    }
  }, [user]);

  useEffect(() => {
    if (!user || !user.id) return;
    poll();
    const id = setInterval(poll, POLL_INTERVAL_MS);
    // Also poll on focus regain (user returns to the tab)
    const onFocus = () => { if (Date.now() - lastPollRef.current > 5000) poll(); };
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(id); window.removeEventListener("focus", onFocus); };
  }, [user, poll]);

  const dismiss = (alertId) => {
    setDismissedAlertIds((prev) => {
      const next = new Set(prev);
      next.add(alertId);
      return next;
    });
  };

  const onAcknowledged = () => { poll(); };
  const onMarkedOnSite = () => { poll(); };

  // Pick the first non-dismissed pending alert to surface in the blocking modal.
  const liveAlert = pending.find((a) => !dismissedAlertIds.has(a.alert_id));
  // Pick the first active visit (acknowledged but not on-site yet) for the sticky banner.
  const liveActive = active[0];

  return (
    <>
      {liveActive ? <CountdownBanner visit={liveActive} onMarkedOnSite={onMarkedOnSite} /> : null}
      {liveAlert ? <PendingCalloutModal alert={liveAlert} onAcknowledged={onAcknowledged} onDismiss={() => dismiss(liveAlert.alert_id)} /> : null}
    </>
  );
}

/* ---------- Helpers ---------- */

function minutesSince(iso) {
  if (!iso) return 0;
  const ms = Date.now() - new Date(iso).getTime();
  return Math.max(0, ms / 60000);
}

function formatRemaining(mins) {
  // mins can be negative when breached
  const sign = mins < 0 ? "-" : "";
  const abs = Math.abs(mins);
  const h = Math.floor(abs / 60);
  const m = Math.floor(abs % 60);
  const s = Math.floor((abs * 60) % 60);
  if (h > 0) return `${sign}${h}h ${String(m).padStart(2, "0")}m ${String(s).padStart(2, "0")}s`;
  return `${sign}${m}m ${String(s).padStart(2, "0")}s`;
}

function urgencyColour(remainingMins) {
  if (remainingMins < 0) return { bg: "#7f1d1d", fg: "#fff", accent: "#fca5a5", label: "BREACHED" };
  if (remainingMins <= 10) return { bg: "#dc2626", fg: "#fff", accent: "#fef2f2", label: "CRITICAL" };
  if (remainingMins <= 30) return { bg: "#d97706", fg: "#fff", accent: "#fef3c7", label: "URGENT" };
  return { bg: "#059669", fg: "#fff", accent: "#d1fae5", label: "ON TRACK" };
}

function useTick(intervalMs = 1000) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
}

/* ---------- Pending modal (blocking) ---------- */

function PendingCalloutModal({ alert, onAcknowledged, onDismiss }) {
  useTick(1000);
  const [eta, setEta] = useState("45");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [muted, setMuted] = useState(false);
  const sinceMin = minutesSince(alert.received_at || alert.sent_at);
  const remaining = SLA_MINUTES - sinceMin;
  const c = urgencyColour(remaining);

  // Two-tone "callout siren" using Web Audio API — no external audio file required.
  // The first user interaction with the page unlocks the audio context (browser policy);
  // on mobile this typically happens on the previous tap that opened the app.
  const audioRef = useRef(null);
  useEffect(() => {
    if (success || muted) return;
    let stopped = false;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    audioRef.current = ctx;
    // Tone helper
    const playPair = (when) => {
      [880, 660].forEach((freq, idx) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "square";
        osc.frequency.value = freq;
        const t0 = when + idx * 0.45;
        gain.gain.setValueAtTime(0, t0);
        gain.gain.linearRampToValueAtTime(0.25, t0 + 0.02);
        gain.gain.setValueAtTime(0.25, t0 + 0.35);
        gain.gain.linearRampToValueAtTime(0, t0 + 0.4);
        osc.connect(gain).connect(ctx.destination);
        osc.start(t0);
        osc.stop(t0 + 0.42);
      });
    };
    // Schedule a loop
    const loop = () => {
      if (stopped) return;
      playPair(ctx.currentTime);
      // 1.2 s cycle
      setTimeout(loop, 1200);
    };
    // Resume in case the context started in suspended state (browser autoplay policy)
    const start = async () => { try { await ctx.resume(); } catch { /* noop */ } loop(); };
    start();
    return () => {
      stopped = true;
      try { ctx.close(); } catch { /* noop */ }
    };
  }, [success, muted]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const n = parseInt(eta, 10);
    if (!n || n < 1 || n > 1440) { setError("ETA must be 1..1440 minutes."); return; }
    setSubmitting(true);
    try {
      await api.post("/nrs/ack/confirm", { token: alert.token, eta_minutes: n });
      setSuccess(true);
      setTimeout(() => { onAcknowledged(); }, 1200);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Unable to acknowledge.");
    } finally { setSubmitting(false); }
  };

  return (
    <div
      data-testid="callout-watcher-modal"
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.85)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 9999,
        backdropFilter: "blur(4px)",
      }}
    >
      <div className="card" style={{ background: "white", maxWidth: 540, width: "100%", padding: 0, boxShadow: "0 20px 60px rgba(0,0,0,0.6)", overflow: "hidden" }}>
        <div style={{ padding: "14px 20px", background: c.bg, color: c.fg, display: "flex", alignItems: "center", gap: 10 }}>
          <AlertTriangle size={20} />
          <div className="mono" style={{ fontWeight: 700, letterSpacing: "0.14em", fontSize: 11 }}>CALLOUT · {alert.tier_label || "ON-CALL"}</div>
          <button
            data-testid="callout-watcher-mute"
            type="button"
            onClick={() => setMuted((m) => !m)}
            title={muted ? "Un-mute alarm" : "Mute alarm"}
            style={{ marginLeft: "auto", background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.4)", color: c.fg, padding: "4px 8px", borderRadius: 3, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700 }}
          >
            {muted ? <VolumeX size={12} /> : <Volume2 size={12} />}
            {muted ? "MUTED" : "ALARM"}
          </button>
          <div className="mono" style={{ fontSize: 12, fontWeight: 700 }}>{c.label}</div>
        </div>

        <div style={{ padding: "16px 20px 4px" }}>
          <div className="mono" style={{ fontSize: 10, color: "#64748b", letterSpacing: "0.12em" }}>TIME REMAINING TO RESPOND</div>
          <div data-testid="callout-watcher-pending-remaining" className="mono" style={{ fontSize: 32, fontWeight: 900, color: c.bg, marginTop: 2 }}>
            {formatRemaining(remaining)}
          </div>
          <div className="mono" style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
            Clock started {alert.received_at ? fmtTimeSeconds(alert.received_at) : "—"} · {sinceMin.toFixed(1)} min ago
          </div>
        </div>

        <div style={{ padding: "0 20px 8px" }}>
          <h2 style={{ margin: "12px 0 6px", fontSize: 20, fontWeight: 900 }}>McCain Whittlesey — out-of-hours callout</h2>
          <div style={{ border: "1px solid #e2e8f0", borderRadius: 6, marginTop: 8, marginBottom: 14, overflow: "hidden" }}>
            <Row icon={<MapPin size={14} />} label="Area" value={alert.area} />
            <Row icon={<Hammer size={14} />} label="Structure" value={alert.structure_ref} />
            <Row icon={<AlertTriangle size={14} />} label="Description" value={alert.description || "(not supplied)"} />
          </div>
        </div>

        {success ? (
          <div data-testid="callout-watcher-success" style={{ padding: 20, background: "#ecfdf5", color: "#065f46", borderTop: "1px solid #d1fae5", textAlign: "center" }}>
            <CheckCircle2 size={28} style={{ margin: "0 auto" }} />
            <div style={{ marginTop: 8, fontWeight: 700 }}>Acknowledged. McCain has been notified — head to site.</div>
          </div>
        ) : (
          <form onSubmit={submit} style={{ padding: "0 20px 20px" }}>
            <label style={{ display: "block", marginBottom: 8 }}>
              <div className="mono" style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>YOUR ETA (MINUTES)</div>
              <input
                data-testid="callout-watcher-eta"
                className="input-base"
                type="number" min={1} max={1440} required autoFocus
                value={eta}
                onChange={(e) => setEta(e.target.value)}
                style={{ fontSize: 22, fontWeight: 900, padding: "8px 12px", width: "100%" }}
              />
            </label>
            {error ? <div data-testid="callout-watcher-error" className="card" style={{ padding: 8, marginBottom: 10, background: "#fef2f2", color: "#b91c1c", borderLeft: "4px solid #dc2626", fontSize: 12 }}><AlertTriangle size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />{error}</div> : null}
            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
              <button type="button" data-testid="callout-watcher-later" onClick={onDismiss} className="btn-ghost" style={{ flex: 0 }}>View later</button>
              <button data-testid="callout-watcher-submit" type="submit" className="btn-primary" disabled={submitting} style={{ flex: 1, padding: "12px 18px", fontWeight: 700 }}>
                {submitting ? "Confirming…" : "Acknowledge & confirm ETA"}
              </button>
            </div>
            <p style={{ margin: "10px 0 0", fontSize: 10, color: "#94a3b8" }}>This dialog will reappear on your next page or after 30 seconds until you acknowledge.</p>
          </form>
        )}
      </div>
    </div>
  );
}

/* ---------- Active sticky banner (post-ack, pre-onsite) ---------- */

function CountdownBanner({ visit, onMarkedOnSite }) {
  useTick(1000);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [editingEta, setEditingEta] = useState(false);
  const [etaDraft, setEtaDraft] = useState(String(visit.eta_minutes || 30));
  const [savingEta, setSavingEta] = useState(false);
  const [etaSavedAt, setEtaSavedAt] = useState(0);
  const sinceMin = minutesSince(visit.received_at);
  const remaining = SLA_MINUTES - sinceMin;
  const c = urgencyColour(remaining);

  const markOnSite = async () => {
    setBusy(true); setError("");
    try {
      await api.post(`/visits/${visit.visit_id}/on-site`);
      onMarkedOnSite();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const saveEta = async () => {
    setError("");
    const n = parseInt(etaDraft, 10);
    if (!n || n < 1 || n > 1440) { setError("ETA must be 1..1440 minutes."); return; }
    setSavingEta(true);
    try {
      await api.post(`/visits/${visit.visit_id}/eta`, { eta_minutes: n });
      setEditingEta(false);
      setEtaSavedAt(Date.now());
      onMarkedOnSite(); // re-poll so banner shows new eta
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update ETA");
    } finally { setSavingEta(false); }
  };

  return (
    <div
      data-testid="callout-watcher-banner"
      style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 200,
        background: c.bg, color: c.fg, padding: "10px 16px",
        display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
        boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", minWidth: 110 }}>
        <div className="mono" style={{ fontSize: 10, letterSpacing: "0.14em", opacity: 0.9 }}>ON CALLOUT · {c.label}</div>
        <div data-testid="callout-watcher-banner-remaining" className="mono" style={{ fontSize: 22, fontWeight: 900 }}>{formatRemaining(remaining)}</div>
      </div>
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ fontWeight: 700, fontSize: 14 }}>{visit.area} · {visit.structure_ref}</div>
        <div style={{ fontSize: 12, opacity: 0.9, marginTop: 2 }}>
          {visit.description || "(no description)"}
          {visit.eta_minutes ? <> · you said ETA ~<strong>{visit.eta_minutes} min</strong></> : null}
          {etaSavedAt && Date.now() - etaSavedAt < 4000 ? <span data-testid="callout-watcher-eta-saved" style={{ marginLeft: 8, padding: "1px 6px", background: "rgba(255,255,255,0.25)", borderRadius: 3, fontSize: 10, fontWeight: 700 }}>ETA UPDATED · McCAIN NOTIFIED</span> : null}
        </div>
        {error ? <div data-testid="callout-watcher-banner-error" style={{ fontSize: 11, marginTop: 4, color: "#fef2f2" }}><AlertTriangle size={11} style={{ verticalAlign: "middle", marginRight: 4 }} />{error}</div> : null}
        {editingEta ? (
          <div style={{ display: "flex", gap: 6, marginTop: 6, alignItems: "center", flexWrap: "wrap" }}>
            <input
              data-testid="callout-watcher-eta-input"
              type="number" min={1} max={1440}
              value={etaDraft}
              onChange={(e) => setEtaDraft(e.target.value)}
              style={{ width: 70, padding: "4px 6px", borderRadius: 3, border: "1px solid rgba(255,255,255,0.4)", background: "rgba(255,255,255,0.95)", color: "#0f172a", fontSize: 13, fontWeight: 700 }}
              autoFocus
            />
            <span style={{ fontSize: 11, opacity: 0.9 }}>min</span>
            <button data-testid="callout-watcher-eta-save" onClick={saveEta} disabled={savingEta} style={{ background: "white", color: c.bg, border: "none", borderRadius: 3, padding: "4px 10px", fontWeight: 800, fontSize: 11, cursor: "pointer" }}>
              {savingEta ? "Saving…" : "Save & notify McCain"}
            </button>
            <button data-testid="callout-watcher-eta-cancel" onClick={() => { setEditingEta(false); setEtaDraft(String(visit.eta_minutes || 30)); setError(""); }} style={{ background: "transparent", color: "#fff", border: "1px solid rgba(255,255,255,0.5)", borderRadius: 3, padding: "4px 10px", fontWeight: 700, fontSize: 11, cursor: "pointer" }}>
              Cancel
            </button>
          </div>
        ) : (
          <button
            data-testid="callout-watcher-eta-edit"
            onClick={() => { setEditingEta(true); setError(""); }}
            style={{ marginTop: 6, background: "transparent", color: "#fff", border: "1px solid rgba(255,255,255,0.55)", borderRadius: 3, padding: "3px 9px", fontSize: 11, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}
          >
            <Clock3 size={11} />Revise ETA
          </button>
        )}
      </div>
      <button
        data-testid="callout-watcher-onsite-btn"
        onClick={markOnSite}
        disabled={busy}
        className="btn-primary"
        style={{ background: "white", color: c.bg, fontWeight: 800, padding: "10px 16px", minWidth: 140 }}
      >
        <Flag size={14} style={{ verticalAlign: -2, marginRight: 6 }} />{busy ? "Saving…" : "Mark on site"}
      </button>
    </div>
  );
}

function Row({ icon, label, value }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", padding: "10px 14px", borderBottom: "1px solid #f1f5f9", alignItems: "flex-start", gap: 12 }}>
      <div className="mono" style={{ fontSize: 10, color: "#64748b", letterSpacing: "0.1em", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 6 }}>{icon}{label}</div>
      <div style={{ color: "#0f172a", fontWeight: 600, fontSize: 14 }}>{value}</div>
    </div>
  );
}
